import dayjs from 'dayjs';

import { IChartRange, IChartTimeframe } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

export const INITIAL_LIMIT = 990;
export const PAGINATION_LIMIT = 990;

/**
 * The chart-klines backend matches `start_time BETWEEN from AND to` against epoch
 * milliseconds, so a caller handing over epoch seconds silently asks for a 1970
 * window and gets an empty chart. That is what blanked the 15m CEX chart for the
 * 2026-09-18 CRYPGPT pairs, so every range is normalised at the request builder.
 */
export const toEpochMs = (value: number): number => {
  if (!Number.isFinite(value)) {
    return value;
  }

  return Math.abs(value) < 1e11 ? Math.round(value * 1000) : Math.round(value);
};

export const chartTimeframeToInterval = (timeframe: IChartTimeframe) => {
  switch (timeframe) {
    case '1s':
      return 1 * 1000;
    case '10s':
      return 10 * 1000;
    case '30s':
      return 30 * 1000;
    case '15m':
      return 900 * 1000;
    case '1m':
      return 60 * 1000;
    case '3m':
      return 180 * 1000;
    case '5m':
      return 300 * 1000;
    case '30m':
      return 1800 * 1000;
    case '1h':
      return 3600 * 1000;
    case '2h':
      return 7200 * 1000;
    case '4h':
      return 14400 * 1000;
    case '6h':
      return 21600 * 1000;
    case '8h':
      return 28800 * 1000;
    case '12h':
      return 43200 * 1000;
    case '24h':
    case '1d':
      return 86400 * 1000;
    case '2d':
      return 172800 * 1000;
    case '3d':
      return 259200 * 1000;
    case '5d':
      return 432000 * 1000;
    case '168h':
    case '1w':
      return 604800 * 1000;
    case '1M':
      return 30 * 86400 * 1000;
  }
};

export const timeframeToRange = (timeframe: IChartTimeframe) => {
  const limit = INITIAL_LIMIT + 1;
  const interval = chartTimeframeToInterval(timeframe);

  const range = Math.floor(limit * interval);

  const endTime = dayjs().valueOf();

  return {
    startTime: endTime - range,
    endTime,
  };
};

const addThousandsSeparator = (numStr: string): string => {
  const [integerPart, decimalPart] = numStr.split('.');
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

export const chartFormatter = (num: number): string => {
  const numStr = normalizeNumber(num, 7);
  const [integerPart, decimalPart] = numStr.split('.');

  if (!decimalPart) {
    return addThousandsSeparator(numStr);
  }

  if (num > 10000) {
    return addThousandsSeparator(integerPart);
  }

  const roundDecimal = (digits: number): string => {
    const factor = Math.pow(10, digits);
    return (Math.round(num * factor) / factor).toString();
  };

  if (num > 100) {
    return addThousandsSeparator(roundDecimal(2));
  }

  if (num > 1) {
    return addThousandsSeparator(roundDecimal(3));
  }

  const firstNonZeroIndex = decimalPart.search(/[1-9]/);
  const digitsAfterZero = Math.min(firstNonZeroIndex + 4, decimalPart.length);

  const result = roundDecimal(digitsAfterZero);

  return addThousandsSeparator(normalizeNumber(result));
};

export const getPeriodToAPI = (period: IChartRange | undefined) => {
  return period ? period.toLowerCase() : undefined;
};
